import React from 'react';
import { Link } from 'react-router-dom';
import HelperGenie from '../components/HelperGenie';

const castingProtocols = [
  {
    name: 'Chromecast',
    description: 'Cast to any Chromecast or Google Cast-enabled device.',
    devices: ['Chromecast (all generations)', 'Chromecast with Google TV', 'Android TV devices', 'Google Nest Hub'],
  },
  {
    name: 'DLNA',
    description: 'Stream to DLNA-certified media devices on your local network.',
    devices: ['Samsung Smart TVs', 'LG Smart TVs', 'Sony Bravia TVs', 'Philips Smart TVs', 'Xbox consoles', 'PlayStation consoles', 'Most DLNA-certified receivers and speakers'],
  },
  {
    name: 'Miracast',
    description: 'Mirror your screen wirelessly to Miracast-compatible displays.',
    devices: ['Roku TVs and streaming sticks', 'Amazon Fire TV and Fire Stick', 'Microsoft Wireless Display Adapter', 'Most Windows PCs and laptops', 'Miracast-enabled Smart TVs'],
  },
];

const faqs = [
  {
    question: 'What Android version is required?',
    answer: 'Zebcast requires Android 8.0 (Oreo) or later. For Miracast support, Android 10 or later is recommended.',
  },
  {
    question: 'My device is not being discovered. What should I do?',
    answer: 'Ensure both your Android device and the receiving device are on the same Wi-Fi network. For DLNA and Chromecast, the router must allow multicast traffic. Try restarting both devices and your router.',
  },
  {
    question: 'Why is my video buffering or laggy?',
    answer: 'This is typically caused by a weak Wi-Fi signal. Move closer to your router, reduce other network traffic, or try using a 5 GHz Wi-Fi band for better performance.',
  },
  {
    question: 'Which video formats are supported?',
    answer: 'Zebcast supports MP4, MKV, AVI, and MOV containers with H.264 and H.265 codecs. Audio codecs supported include AAC, MP3, and AC3.',
  },
  {
    question: 'Can I cast DRM-protected content?',
    answer: 'DRM-protected content (e.g. from Netflix, Disney+) cannot be cast via Zebcast due to content protection restrictions. Use the native app\'s built-in cast feature for those services.',
  },
  {
    question: 'How do I send feedback or report a bug?',
    answer: 'Email support@zebcast.com with a short description and, if possible, the device model, Android version, and the receiver you were casting to. Screenshots or a screen recording help us reproduce the problem faster.',
  },
];

const Support = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-zebcast-purple to-zebcast-dark px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 mb-8">
          <div className="text-center mb-8">
            <Link to="/" className="text-4xl font-bold text-gray-900 hover:text-zebcast-purple transition-colors">
              Zebcast
            </Link>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Support</h1>
          <p className="text-gray-500 mb-6">
            Get help with Zebcast, check device compatibility, or contact our team.
          </p>

          {/* Contact card */}
          <div className="bg-gradient-to-r from-zebcast-purple to-zebcast-dark rounded-xl p-6 text-white">
            <h2 className="text-xl font-semibold mb-2">Need help?</h2>
            <p className="mb-4 opacity-90">
              Our support team is here to assist you with any issues or questions.
            </p>
            <a
              href="mailto:support@zebcast.com"
              className="inline-block bg-white text-zebcast-purple font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Email support@zebcast.com
            </a>
          </div>
        </div>

        {/* Device Support */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Supported Devices & Protocols</h2>

          <div className="space-y-8">
            {castingProtocols.map((protocol) => (
              <div key={protocol.name} className="border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{protocol.name}</h3>
                <p className="text-gray-600 mb-4">{protocol.description}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {protocol.devices.map((device) => (
                    <li key={device} className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-zebcast-purple rounded-full mr-3 flex-shrink-0"></span>
                      {device}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-800">Sender requirement:</span> Android 8.0 (Oreo) or later.
              Zebcast works with any Android phone or tablet that supports the casting protocol of your target device.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer links */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/" className="text-zebcast-purple hover:text-zebcast-dark font-medium transition-colors">
              &larr; Back to Zebcast
            </Link>
            <Link to="/privacy" className="text-zebcast-purple hover:text-zebcast-dark font-medium transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
      <HelperGenie />
    </div>
  );
};

export default Support;
